// Offline context + provider (P9.3/P9.5). Tracks connectivity (navigator + active probe), runs
// catalog sync and queue flush, listens to Background-Sync messages, and renders the global status bar.
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useCatalogSync } from "@/hooks/useCatalogSync";
import { useQueueFlush } from "@/hooks/useQueueFlush";
import { useOfflinePos } from "@/hooks/useOfflinePos";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { requestQueueFlushSync } from "@/lib/swRegister";
import { pendingSales } from "@/lib/offlineDb";
import { OfflineStatusBar } from "@/components/pos/OfflineStatusBar";

const OfflineCtx = createContext(null);
export const useOffline = () => useContext(OfflineCtx) || { online: true };

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const probing = useRef(false);

  const catalog = useCatalogSync(online);
  const queue = useQueueFlush(online);
  const offlinePos = useOfflinePos(online);
  const install = useInstallPrompt();

  const refreshPending = useCallback(async () => {
    try { setPending((await pendingSales()).length); } catch (_) {}
  }, []);

  // Active connectivity probe (navigator.onLine is unreliable).
  const probe = useCallback(async () => {
    if (probing.current) return;
    probing.current = true;
    try {
      await api.get("/health", { timeout: 4000 });
      setOnline(true);
    } catch (e) {
      // a 4xx still means we reached the server → online
      setOnline(!!e?.response);
    } finally {
      probing.current = false;
    }
  }, []);

  const flushNow = useCallback(async () => {
    const r = await queue.flush();
    await refreshPending();
    return r;
  }, [queue, refreshPending]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); probe(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const onMsg = (e) => { if (e.data?.type === "POS_QUEUE_FLUSH") flushNow(); };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    const t = setInterval(probe, 15000);
    probe(); refreshPending();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      navigator.serviceWorker?.removeEventListener?.("message", onMsg);
      clearInterval(t);
    };
  }, [probe, flushNow, refreshPending]);

  // On reconnect → flush queue and request a background sync.
  useEffect(() => {
    if (online) { flushNow(); requestQueueFlushSync(); }
  }, [online, flushNow]);

  const value = { online, pending, refreshPending, catalog, queue: { ...queue, flushNow },
                  offlinePos, install, probe };

  return (
    <OfflineCtx.Provider value={value}>
      {children}
      <OfflineStatusBar />
    </OfflineCtx.Provider>
  );
}
